import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SITE_NAME_ZH = '找搭子网';
const SITE_NAME_EN = 'Zaodazi';
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://www.zaodazi.com';
const DEFAULT_OG_IMAGE = '/images/og-image.jpg';

const SEO_BY_LANG = {
  zh: {
    siteName: SITE_NAME_ZH,
    description: '旅游搭子 - 找到志同道合的旅伴，一起出发！找搭子，旅游交友，结伴旅行，旅行计划。',
    keywords: '找搭子,旅游搭子,搭子,旅游,旅行计划,结伴旅游,旅游交友,旅行伙伴,自由行,驴友,旅行结伴,组队旅行',
    ogLocale: 'zh_CN',
  },
  en: {
    siteName: SITE_NAME_EN,
    description: 'Find your perfect travel buddy! Travel partner matching, trip planning, and buddy travel made easy.',
    keywords: 'travel buddy, travel partner, find travel companion, trip planner, travel mate, buddy travel, travel friend, travel matching, travel together, vacation buddy',
    ogLocale: 'en_US',
  },
  ja: {
    siteName: 'Zaodazi',
    description: '理想の旅行仲間を見つけよう！旅行計画、旅行仲間マッチング、一緒に旅する仲間探し。',
    keywords: '旅行仲間,旅友達,旅行パートナー,旅行計画,旅行マッチング,旅行ともだち,一緒に旅行',
    ogLocale: 'ja_JP',
  },
  es: {
    siteName: SITE_NAME_EN,
    description: '¡Encuentra tu compañero de viaje perfecto! Planificación de viajes y matching de viajeros.',
    keywords: 'compañero de viaje, encontrar compañero de viaje, planificador de viajes, viajar juntos, amigo de viaje',
    ogLocale: 'es_ES',
  },
  fr: {
    siteName: SITE_NAME_EN,
    description: 'Trouvez votre compagnon de voyage idéal ! Planification de voyage et mise en relation de voyageurs.',
    keywords: 'compagnon de voyage, trouver compagnon de voyage, planification de voyage, voyager ensemble, ami de voyage',
    ogLocale: 'fr_FR',
  },
  de: {
    siteName: SITE_NAME_EN,
    description: 'Finde deinen perfekten Reisebegleiter! Reiseplanung und Reisepartner-Vermittlung.',
    keywords: 'Reisebegleiter, Reisegefährte, Reiseplanung, Reisepartner, zusammen reisen, Reisefreund',
    ogLocale: 'de_DE',
  },
  pt: {
    siteName: SITE_NAME_EN,
    description: 'Encontre seu companheiro de viagem perfeito! Planejamento de viagens e matching de viajantes.',
    keywords: 'companheiro de viagem, encontrar parceiro de viagem, planejamento de viagem, viajar junto, amigo de viagem',
    ogLocale: 'pt_BR',
  },
  ru: {
    siteName: SITE_NAME_EN,
    description: 'Найдите идеального попутчика! Планирование путешествий и подбор попутчиков.',
    keywords: 'попутчик, найти попутчика, планирование путешествий, путешествовать вместе, компаньон по путешествию',
    ogLocale: 'ru_RU',
  },
};

const Seo = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = 'website',
}) => {
  const { i18n } = useTranslation();
  const rawLang = i18n.language || 'zh';
  const langKey = Object.keys(SEO_BY_LANG).includes(rawLang) ? rawLang : 'zh';
  const seo = SEO_BY_LANG[langKey];
  const lang = langKey === 'zh' ? 'zh-CN' : langKey === 'en' ? 'en' : langKey;
  const pageTitle = title ? `${title} - ${seo.siteName}` : `${seo.siteName} - ${seo.description.slice(0, 50)}`;
  const pageDescription = description || seo.description;
  const pageKeywords = keywords || seo.keywords;
  const canonicalUrl = canonical || SITE_URL;
  const imageUrl = ogImage || `${SITE_URL}${DEFAULT_OG_IMAGE}`;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={seo.siteName} />
      <meta property="og:locale" content={seo.ogLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
};

export default Seo;
